/**
 * The browse tab strip — ARTISTS · GENRES · ALBUMS.
 *
 * Extracted because the strip is rendered by THREE separate views
 * (`ArtistsList`, `GenresList`, `AlbumsList`) that each own a different route.
 * It was duplicated in two of them, and adding the third tab would have meant
 * editing the same markup in three files and keeping the `selected` flags,
 * accents, hrefs and testids in sync by hand — exactly the drift a shared
 * builder removes. Each tab is a LINK to its own route, so switching sections is
 * a real navigation the router re-renders (and therefore a real history entry
 * that Back can return through).
 *
 * @module
 */

import { TESTIDS } from '@streamymusic/shared';

import { Tab } from '../../components/primitives/Chip.js';
import { append, el } from '../../components/primitives/dom.js';

/**
 * The three browse sections, in tab order. Ordered artists-first to match the
 * §3 crawl (ARTISTS is the `/browse` hub's redirect target).
 *
 * @type {ReadonlyArray<{ id: 'artists'|'genres'|'albums', label: string, accent: 'pink'|'purple'|'yellow', href: string, testId: string }>}
 */
export const BROWSE_TABS = [
  {
    id: 'artists',
    label: 'ARTISTS',
    accent: 'pink',
    href: '#/browse/artists',
    testId: TESTIDS.ARTISTS_LINK,
  },
  {
    id: 'genres',
    label: 'GENRES',
    accent: 'purple',
    href: '#/browse/genres',
    testId: TESTIDS.GENRES_LINK,
  },
  {
    id: 'albums',
    label: 'ALBUMS',
    // `yellow` — the third of the three LINK-tab treatments the mock defines
    // (primitives/Chip.js rejects anything else for a link tab).
    accent: 'yellow',
    href: '#/browse/albums',
    testId: TESTIDS.ALBUMS_LINK,
  },
];

/**
 * @param {'artists'|'genres'|'albums'} active which section is on screen
 * @returns {HTMLElement} the `role="tablist"` strip
 */
export function BrowseTabs(active) {
  const tabs = el('div', {
    className: 'sm-tab-row',
    attrs: { role: 'tablist', 'aria-label': 'Browse sections' },
  });
  append(
    tabs,
    ...BROWSE_TABS.map((tab) =>
      Tab({
        label: tab.label,
        accent: tab.accent,
        selected: tab.id === active,
        href: tab.href,
        testId: tab.testId,
      }),
    ),
  );
  return tabs;
}
